// src/lib/appwrite/trustScore.ts
import { ID, Query } from 'appwrite';
import { appwriteConfig, databases } from './config';
import { Rating, TrustScore, TrustScoreEvent } from '@/types';
import { notificationService } from '@/services/NotificationService';


// Get user's TrustScore
export async function getUserTrustScore(userId: string): Promise<TrustScore> {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.trustScoreCollectionId,
      [Query.equal('userId', userId)]
    );
    
    if (response.documents.length > 0) {
      return response.documents[0] as unknown as TrustScore;
    }
    
    // Create new TrustScore if doesn't exist
    const newTrustScore = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.trustScoreCollectionId,
      ID.unique(),
      {
        userId,
        score: 50,
        level: 'bronze',
        type: 'buyer',
        updatedAt: new Date().toISOString()
      }
    );
    
    return newTrustScore as unknown as TrustScore;
  } catch (error) {
    console.error('Error getting user trust score:', error);
    throw error;
  }
}

// Log a TrustScore event
export async function logTrustScoreEvent(
  userId: string,
  eventType: string,
  points: number,
  description: string,
  relatedId?: string
): Promise<TrustScoreEvent> {
  try {
    // First update the user's trust score
    const currentScore = await getUserTrustScore(userId);
    const newScore = currentScore.score + points;
    
    // Determine level based on new score
    let level = currentScore.level;
    if (newScore >= 5000) level = 'legend';
    else if (newScore >= 3200) level = 'diamond';
    else if (newScore >= 2000) level = 'gold';
    else if (newScore >= 1100) level = 'silver';
    
    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.trustScoreCollectionId,
      currentScore.$id,
      {
        score: newScore,
        level,
        updatedAt: new Date().toISOString()
      }
    );
    
    // Then log the event
    const event = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.trustScoreEventsCollectionId,
      ID.unique(),
      {
        userId,
        eventType,
        description,
        points,
        relatedId,
        createdAt: new Date().toISOString()
      }
    );
    
    return event as unknown as TrustScoreEvent;
  } catch (error) {
    console.error('Error logging trust score event:', error);
    throw error;
  }
}

// Submit a rating
export async function submitRating(
  userName: string,
  dealId: string,
  reviewerId: string,
  recipientId: string,
  rating: number,
  comment?: string
): Promise<Rating> {
  try {
    // Create the rating
    const newRating = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.ratingsCollectionId,
      ID.unique(),
      {
        dealId,
        reviewerId,
        recipientId,
        rating,
        comment,
        createdAt: new Date().toISOString()
      }
    );
      notificationService.createNotification({
        title: "Rating",
        body: `Received ${rating}★ rating from ${userName}`,
        type: "deal",
        recipient_id: recipientId,
        sender_id: reviewerId,
      });
      
    // Determine points based on rating (for sellers)
    if (rating >= 4) {
      await logTrustScoreEvent(
        recipientId,
        'positive_review',
        5,
        `Received ${rating}★ rating from ${userName}`,
        dealId
      );
    } else if (rating <= 2) {
      await logTrustScoreEvent(
        recipientId,
        'negative_review',
        -10,
        `Received ${rating}★ rating from ${userName}`,
        dealId
      );
    }
    
    return newRating as unknown as Rating;
  } catch (error) {
    console.error('Error submitting rating:', error);
    throw error;
  }
}

// Get user's rating history
export async function getUserRatings(userId: string): Promise<Rating[]> {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.ratingsCollectionId,
      [Query.equal('recipientId', userId)]
    );
    
    return response.documents as unknown as Rating[];
  } catch (error) {
    console.error('Error getting user ratings:', error);
    throw error;
  }
}

// Get user's TrustScore events
export async function getUserTrustScoreEvents(userId: string): Promise<TrustScoreEvent[]> {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.trustScoreEventsCollectionId,
      [
        Query.equal('userId', userId),
        Query.orderDesc('createdAt')
      ]
    );
    
    return response.documents as unknown as TrustScoreEvent[];
  } catch (error) {
    console.error('Error getting trust score events:', error);
    throw error;
  }
}

// Calculate average rating
export async function getUserAverageRating(userId: string): Promise<{ average: number; count: number }> {
  try {
    const ratings = await getUserRatings(userId);
    if (ratings.length === 0) return { average: 0, count: 0 };
    
    const sum = ratings.reduce((acc, rating) => acc + rating.rating, 0);
    const average = sum / ratings.length;
    
    return { average, count: ratings.length };
  } catch (error) {
    console.error('Error calculating average rating:', error);
    throw error;
  }
}

export async function checkExistingRating(
  dealId: string,
  userId: string,
  recipientId: string
): Promise<boolean> {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.ratingsCollectionId,
      [
        Query.equal('dealId', dealId),
        Query.equal('reviewerId', userId),
        Query.equal('recipientId', recipientId)
      ]
    );
    
    return response.documents.length > 0;
  } catch (error) {
    console.error('Error checking existing rating:', error);
    return false;
  }
}

export async function generateReferralCode(userId: string) {
  try {
    // Generate a random 8-character code
    const code = Math.random().toString(36).substring(2, 10).toUpperCase();
    
    // Save to database
    await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.referralCodesCollectionId,
      ID.unique(),
      {
        userId,
        code,
        createdAt: new Date().toISOString(),
        isActive: true
      }
    );
    
    return code;
  } catch (error) {
    console.error('Error generating referral code:', error);
    throw error;
  }
}

// Buy trust points
export async function buyTrustPoints(userId: string, points: number, amount: number) {
  try {
    // Process payment (you'll integrate with your payment gateway here)
    // Then award points
    await logTrustScoreEvent(
      userId,
      'points_purchase',
      points,
      `Purchased ${points} TrustPoints`
    );
    
    // Record transaction
    await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.transactionsCollectionId,
      ID.unique(),
      {
        userId,
        points,
        amount,
        status: 'completed',
        createdAt: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error('Error buying trust points:', error);
    throw error;
  }
}

export async function markReferralRedeemed(userId: string) {
  try {
    // First get the current trust score document
    const trustScore = await getUserTrustScore(userId);
    
    // Update the hasRedeemed flag
    const response = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.trustScoreCollectionId,
      trustScore.$id,
      { hasRedeemed: true }
    );
    
    return response;
  } catch (error) {
    console.error('Error marking referral as redeemed:', error);
    throw error;
  }
}

export async function getReferralCode(code: string) {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.referralCodesCollectionId,
      [Query.equal('code', code)]
    );
    return response.documents[0];
  } catch (error) {
    console.error('Error getting referral code:', error);
    throw error;
  }
}

// Update referral code after redemption
export async function updateReferralCodeUsage(codeId: string) {
  try {
    const response = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.referralCodesCollectionId,
      codeId,
      {
        isActive: false, // Optional: disable code after single use
      }
    );
    return response;
  } catch (error) {
    console.error('Error updating referral code usage:', error);
    throw error;
  }
}

export async function getUserReferralCode(userId: string) {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.referralCodesCollectionId,
      [Query.equal('userId', userId)]
    );
    return response;
  } catch (error) {
    console.error('Error getting user referral code:', error);
    throw error;
  }
}